import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconLoader2,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';

import {
  ButtonField,
  ButtonFieldIcon,
  ButtonGroup,
  ButtonGroupItem,
  CheckboxField,
  CheckboxGroupField,
  Iconsvg,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PaginationBar,
  PaginationBarLeft,
  PaginationBarRight,
  PillSuggestion,
  SelectField,
} from '@wk/components-react16';

import {
  createOrrScoreDroppedSubscriptions,
  deleteAlertSubscription,
  fetchMyAlertSubscriptions,
  fetchNotificationOptions,
  updateAlertSubscriptionChannels,
  type AlertSubscription,
  type CreateAlertSubscriptionRequest,
} from '@/domains/alerts';
import MultiSelect from '@/shared/ui/MultiSelect';
import useApplicationsStore from '@/domains/application/applicationStore';
import useToast from '@/shared/stores/toastStore';
import { ENVIRONMENTS, ENVIRONMENT_ACRONYMS } from '@/config/constants';

const PAGE_SIZES = [30, 50, 100, 200];

// Maps a SystemConfiguration alert-type key to the API call that creates it.
// Types present here are offerable in the modal; types the backend exposes but
// we have no creator for are surfaced as read-only (cannot be created yet).
const CREATE_BY_TYPE: Record<
  string,
  typeof createOrrScoreDroppedSubscriptions
> = {
  orr_score_dropped: createOrrScoreDroppedSubscriptions,
};

// The only alert type today. Map the stored value to a friendly label, falling
// back to a generic humaniser so an unknown future type still reads sensibly.
const ALERT_TYPE_LABEL: Record<string, string> = {
  'orr-score-dropped': 'ORR score dropped',
  orrscoredropped: 'ORR score dropped',
};
const alertTypeLabel = (value: string) => {
  if (!value) return '—';
  const known = ALERT_TYPE_LABEL[value.toLowerCase()];
  if (known) return known;
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\borr\b/gi, 'ORR');
};

const sortedKey = (channels: string[]) => [...channels].sort().join(',');

const apiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as AxiosError | undefined)?.response?.data as
    | { title?: string; message?: string }
    | undefined;
  return data?.title || data?.message || fallback;
};

// Channel pills shown in the grid/cards, mirroring the env/role badge look.
const renderChannelChips = (
  channels: string[],
  labelOf: (key: string) => string,
) => (
  <div className="flex flex-wrap items-center gap-2">
    {channels.length === 0 ? (
      <span className="text-sm text-gray-500">—</span>
    ) : (
      channels.map((channel) => (
        <span key={channel} className="roleBadge roleBadge--blue">
          <PillSuggestion size="small">{labelOf(channel)}</PillSuggestion>
        </span>
      ))
    )}
  </div>
);

const renderEnvBadge = (environment: string) => (
  <span className={`envBadge envBadge--${environment.toLowerCase()}`}>
    <PillSuggestion size="small">
      {ENVIRONMENT_ACRONYMS[
        environment.toLowerCase() as keyof typeof ENVIRONMENT_ACRONYMS
      ] ?? environment.slice(0, 3).toLowerCase()}
    </PillSuggestion>
  </span>
);

const AlertsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['alert-subscriptions', 'mine'] });

  const subscriptionsQ = useQuery({
    queryKey: ['alert-subscriptions', 'mine'],
    queryFn: async () => (await fetchMyAlertSubscriptions()).data,
  });
  const subscriptions = subscriptionsQ.data ?? [];

  // Accepted channels are data-driven from the public options endpoint
  // (SystemConfiguration.notifications) — no hard-coded list.
  const { data: optionsData, isLoading: optionsLoading } = useQuery({
    queryKey: ['notification-options'],
    queryFn: async () => (await fetchNotificationOptions()).data,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const availableChannels = useMemo(
    () => optionsData?.channels?.filter((c) => c.enabled).map((c) => c.key) ?? [],
    [optionsData],
  );
  const channelLabel = (key: string) =>
    optionsData?.channels?.find((c) => c.key === key)?.label ?? key;
  const optionsReady = availableChannels.length > 0;

  // Alert types are data-driven from SystemConfiguration.notifications too — the
  // modal title and the "Alert type" picker come from here, not a hard-coded
  // string. Only types we know how to create are offered (see CREATE_BY_TYPE).
  const availableTypes = useMemo(
    () =>
      (optionsData?.types ?? []).filter(
        (t) => t.enabled && t.key in CREATE_BY_TYPE,
      ),
    [optionsData],
  );
  const typeLabel = (key: string) =>
    optionsData?.types?.find((t) => t.key === key)?.label ??
    alertTypeLabel(key);

  // Reuse the home screen's application source (inventory `/applications`)
  // so the BIT Id search and Application name are filled from the same list.
  const applications = useApplicationsStore((state) => state.applications);

  // Options for the shared MultiSelect (same pattern as the Evaluations page):
  // the value is the BIT Id, the dropdown shows the name with the BIT Id below,
  // and the selected pills show the application name.
  const applicationOptions = useMemo(
    () =>
      applications.map((application) => ({
        label: application.name,
        value: application.bitId,
        render: (option: { value: string; label: string }) => (
          <span className="flex w-full flex-col gap-1" key={option.value}>
            <span className="w-full truncate text-sm font-medium">
              {option.label}
            </span>
            <span className="text-xs text-muted-foreground">{option.value}</span>
          </span>
        ),
      })),
    [applications],
  );
  const nameByBitId = useMemo(
    () => new Map(applications.map((a) => [a.bitId, a.name])),
    [applications],
  );

  // Editor (create + edit) modal state.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AlertSubscription | null>(null);
  const [alertType, setAlertType] = useState('');
  // Edit identifies a single existing subscription (these come from editTarget).
  const [applicationId, setApplicationId] = useState('');
  const [applicationName, setApplicationName] = useState('');
  // Create can target several applications at once (each becomes one request).
  // We track the selected BIT Ids; the name comes from the applications list.
  const [selectedBitIds, setSelectedBitIds] = useState<string[]>([]);
  const [environment, setEnvironment] = useState('Production');
  const [channels, setChannels] = useState<string[]>([]);

  // Delete confirmation modal state.
  const [deleteTarget, setDeleteTarget] = useState<AlertSubscription | null>(
    null,
  );

  const [filterTerm, setFilterTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  // Below the `sm` breakpoint AG Grid has no height to fill, so we render cards.
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia('(max-width: 640px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const onChange = (event: MediaQueryListEvent) =>
      setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  const isEdit = editTarget !== null;

  const closeEditor = () => {
    setEditorOpen(false);
    setEditTarget(null);
    setAlertType('');
    setApplicationId('');
    setApplicationName('');
    setSelectedBitIds([]);
    setEnvironment('Production');
    setChannels([]);
  };

  const openCreate = () => {
    setEditTarget(null);
    setAlertType(availableTypes[0]?.key ?? '');
    setApplicationId('');
    setApplicationName('');
    setSelectedBitIds([]);
    setEnvironment('Production');
    setChannels([]);
    setEditorOpen(true);
  };

  const openEdit = (subscription: AlertSubscription) => {
    setEditTarget(subscription);
    setAlertType(subscription.alertType ?? '');
    setApplicationId(subscription.details?.application_id ?? '');
    setApplicationName(subscription.details?.application_name ?? '');
    setEnvironment(subscription.details?.environment ?? 'Production');
    setChannels(subscription.alertChannels ?? []);
    setEditorOpen(true);
  };

  // The options query may resolve after the modal opened (first click): seed the
  // type from data once it arrives so the picker is never left blank on create.
  useEffect(() => {
    if (editorOpen && !isEdit && !alertType && availableTypes.length > 0) {
      setAlertType(availableTypes[0].key);
    }
  }, [editorOpen, isEdit, alertType, availableTypes]);

  const toggleChannel = (channel: string) =>
    setChannels((current) =>
      current.includes(channel)
        ? current.filter((c) => c !== channel)
        : [...current, channel],
    );

  const createMutation = useMutation({
    mutationFn: (requests: CreateAlertSubscriptionRequest[]) => {
      const create = CREATE_BY_TYPE[alertType];
      if (!create) {
        return Promise.reject(
          new Error(`No creator registered for alert type "${alertType}".`),
        );
      }
      return create(requests);
    },
    onSuccess: (response, requests) => {
      invalidate();
      // The API returns only the subscriptions it actually created; any that
      // already existed are silently skipped, so requested may exceed created.
      const requested = requests.length;
      const created = response.data?.length ?? requested;
      const skipped = Math.max(0, requested - created);
      if (created === 0) {
        showToast('info', {
          title: 'Already subscribed',
          message:
            requested === 1
              ? `You were already subscribed for ${requests[0].applicationName} in ${environment}.`
              : `All ${requested} selected applications were already subscribed in ${environment}.`,
        });
      } else {
        showToast('success', {
          title:
            created === 1
              ? 'Subscription created'
              : `${created} subscriptions created`,
          message:
            requested === 1
              ? `You'll be alerted for "${typeLabel(alertType)}" on ${requests[0].applicationName} in ${environment}.`
              : `Created ${created} of ${requested} ${typeLabel(
                  alertType,
                )} alerts in ${environment}${
                  skipped ? ` — ${skipped} already existed.` : '.'
                }`,
        });
      }
      closeEditor();
    },
    onError: (error) => {
      showToast('error', {
        title: 'Could not create the subscription',
        message: apiErrorMessage(
          error,
          'It may already exist for this application and environment.',
        ),
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (next: string[]) =>
      updateAlertSubscriptionChannels(editTarget!.id, next),
    onSuccess: () => {
      invalidate();
      showToast('success', {
        title: 'Subscription updated',
        message: `Channels updated for ${applicationName}.`,
      });
      closeEditor();
    },
    onError: (error) => {
      showToast('error', {
        title: 'Could not update the subscription',
        message: apiErrorMessage(error, 'An error occurred while saving.'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAlertSubscription(id),
    onSuccess: () => {
      invalidate();
      showToast('success', {
        title: 'Subscription removed',
        message: `Removed the ${alertTypeLabel(
          deleteTarget?.alertType ?? '',
        ).toLowerCase()} alert for ${deleteTarget?.details?.application_name ?? 'this application'}.`,
      });
      setDeleteTarget(null);
    },
    onError: (error) => {
      showToast('error', {
        title: 'Could not remove the subscription',
        message: apiErrorMessage(error, 'An error occurred while removing it.'),
      });
    },
  });

  const editorDirty = isEdit
    ? sortedKey(channels) !== sortedKey(editTarget?.alertChannels ?? [])
    : true;
  const canSubmit =
    channels.length > 0 &&
    editorDirty &&
    (isEdit ||
      (alertType in CREATE_BY_TYPE &&
        selectedBitIds.length > 0 &&
        environment.length > 0));
  const submitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    if (isEdit) {
      updateMutation.mutate(channels);
    } else {
      createMutation.mutate(
        selectedBitIds.map((bitId) => ({
          applicationId: bitId,
          applicationName: nameByBitId.get(bitId) ?? bitId,
          environment,
          alertChannels: channels,
        })),
      );
    }
  };

  const filteredSubscriptions = useMemo(() => {
    const needle = filterTerm.trim().toLowerCase();
    if (!needle) return subscriptions;
    return subscriptions.filter((s) =>
      [
        s.details?.application_name ?? '',
        s.details?.application_id ?? '',
      ].some((field) => field.toLowerCase().includes(needle)),
    );
  }, [subscriptions, filterTerm]);

  const pageCount = Math.max(1, Math.ceil(filteredSubscriptions.length / limit));
  useEffect(() => setPage(1), [filterTerm]);
  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);
  const pagedSubscriptions = filteredSubscriptions.slice(
    (page - 1) * limit,
    page * limit,
  );

  const renderAppIdentity = (subscription: AlertSubscription) => (
    <>
      <span className="font-medium">
        {subscription.details?.application_name ||
          subscription.details?.application_id ||
          '—'}
      </span>
      <span className="block truncate text-xs text-gray-500">
        {subscription.details?.application_id ?? '—'}
      </span>
    </>
  );

  const renderActions = (subscription: AlertSubscription) => (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-label="Edit subscription"
        title="Edit subscription"
        onClick={() => openEdit(subscription)}
        className="flex h-7 w-7 cursor-pointer items-center justify-center text-gray-500 hover:text-gray-800"
      >
        <IconPencil size={16} />
      </button>
      <button
        type="button"
        aria-label="Delete subscription"
        title="Delete subscription"
        onClick={() => setDeleteTarget(subscription)}
        className="flex h-7 w-7 cursor-pointer items-center justify-center text-gray-500 hover:text-red-600"
      >
        <IconTrash size={16} />
      </button>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 px-4 lg:pr-8 py-4 h-full max-sm:h-auto max-sm:gap-3 max-sm:px-0 max-sm:py-0">
      <div className="flex items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ButtonFieldIcon>
            <button
              onClick={() => navigate('/settings')}
              aria-label="Back to settings"
            >
              <IconArrowLeft size={28} />
            </button>
          </ButtonFieldIcon>
          Alert Subscriptions
        </h1>
      </div>
      <p className="text-sm text-gray-500 -mt-2">
        Get notified when an application&apos;s ORR score drops below 100% in a
        given environment.
      </p>

      <div className="flex items-end gap-3">
        <div className="cg-field flex min-w-0 flex-1 flex-col sm:max-w-sm">
          <label className="cg-field-label" htmlFor="alert-filter">
            Filter
          </label>
          <div className="group border border-input text-sm hover:border-[#005b92] hover:shadow-[0_0_0_.0625rem_#005b92] focus-within:border-[#000] focus-within:shadow-[0_0_0_.0625rem_#000] focus-within:hover:border-[#000] focus-within:hover:shadow-[0_0_0_.0625rem_#000]">
            <div className="flex min-h-8 items-center gap-1 px-[15px] py-1">
              <input
                id="alert-filter"
                type="text"
                name="filter"
                value={filterTerm}
                onChange={(event) => setFilterTerm(event.target.value)}
                placeholder="Filter by application or BIT Id"
                autoComplete="off"
                className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:ml-6">
          <ButtonField mode="text">
            <button
              type="button"
              aria-label="New subscription"
              onClick={openCreate}
            >
              <span className="flex items-center gap-2">
                <Iconsvg name="plus" />
                <span className="max-sm:hidden">New subscription</span>
              </span>
            </button>
          </ButtonField>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {subscriptionsQ.isError ? (
          <div className="rounded-md border bg-white p-4 text-sm text-red-600">
            Failed to load subscriptions.
          </div>
        ) : isMobileViewport ? (
          <div className="flex flex-col gap-3">
            {subscriptionsQ.isLoading ? (
              <div className="flex min-h-[160px] items-center justify-center text-gray-500">
                <IconLoader2 size={22} className="animate-spin" />
              </div>
            ) : pagedSubscriptions.length === 0 ? (
              <div className="rounded-md border bg-white p-4 text-sm text-gray-600">
                You have no alert subscriptions yet.
              </div>
            ) : (
              pagedSubscriptions.map((subscription) => (
                <article
                  key={subscription.id}
                  className="rounded-lg border bg-white p-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 leading-tight">
                      {renderAppIdentity(subscription)}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {renderEnvBadge(
                        subscription.details?.environment ?? '',
                      )}
                      {renderActions(subscription)}
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {typeLabel(subscription.alertType)}
                  </p>
                  <div className="mt-2">
                    {renderChannelChips(
                      subscription.alertChannels ?? [],
                      channelLabel,
                    )}
                  </div>
                </article>
              ))
            )}
          </div>
        ) : (
          <div className="cg-ag-grid-field ag-theme-balham min-h-0 flex-1">
            <AgGridReact
              animateRows={false}
              loading={subscriptionsQ.isLoading}
              rowData={pagedSubscriptions}
              overlayNoRowsTemplate="You have no alert subscriptions yet."
              columnDefs={[
                {
                  headerName: 'Application',
                  field: 'details.application_name',
                  flex: 2,
                  minWidth: 260,
                  autoHeight: true,
                  cellRenderer: (
                    params: CustomCellRendererProps<AlertSubscription>,
                  ) => {
                    const subscription = params.data;
                    if (!subscription) return null;
                    return (
                      <div className="flex flex-col py-1 leading-tight">
                        {renderAppIdentity(subscription)}
                      </div>
                    );
                  },
                },
                {
                  headerName: 'Environment',
                  field: 'details.environment',
                  width: 160,
                  cellRenderer: (
                    params: CustomCellRendererProps<AlertSubscription>,
                  ) => {
                    const env = params.data?.details?.environment;
                    if (!env) return null;
                    return (
                      <div className="flex h-full items-center">
                        {renderEnvBadge(env)}
                      </div>
                    );
                  },
                },
                {
                  headerName: 'Alert type',
                  field: 'alertType',
                  width: 220,
                  valueFormatter: (params) => typeLabel(params.value),
                },
                {
                  headerName: 'Channels',
                  field: 'alertChannels',
                  flex: 2,
                  minWidth: 200,
                  sortable: false,
                  autoHeight: true,
                  cellRenderer: (
                    params: CustomCellRendererProps<AlertSubscription>,
                  ) => {
                    const subscription = params.data;
                    if (!subscription) return null;
                    return (
                      <div className="py-2">
                        {renderChannelChips(
                          subscription.alertChannels ?? [],
                          channelLabel,
                        )}
                      </div>
                    );
                  },
                },
                {
                  headerName: 'Actions',
                  colId: 'actions',
                  width: 110,
                  sortable: false,
                  cellRenderer: (
                    params: CustomCellRendererProps<AlertSubscription>,
                  ) => {
                    const subscription = params.data;
                    if (!subscription) return null;
                    return (
                      <div className="flex h-full items-center">
                        {renderActions(subscription)}
                      </div>
                    );
                  },
                },
              ]}
            />
          </div>
        )}

        <PaginationBar>
          <PaginationBarLeft>
            {filteredSubscriptions.length} results
          </PaginationBarLeft>
          <PaginationBarRight>
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">
                Page {page} of {pageCount}
              </p>
              <SelectField size="small">
                <select
                  value={limit}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                    setLimit(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </SelectField>
              <Pagination
                pageIndex={page}
                pageCount={pageCount}
                onUserRequest={(next: number) => setPage(next)}
              />
            </div>
          </PaginationBarRight>
        </PaginationBar>
      </div>

      {/* Create / edit subscription modal. On edit the application and environment
          are fixed (they identify the subscription); only channels are editable. */}
      <Modal
        isOpen={editorOpen}
        mobileMode="fullscreen"
        srLabelledBy="alert-editor-title"
        srDescribedBy="alert-editor-desc"
        onUserRequest={closeEditor}
      >
        <ModalHeader titleId="alert-editor-title" onUserRequest={closeEditor}>
          {isEdit ? 'Edit alert subscription' : 'New alert subscription'}
        </ModalHeader>
        <ModalBody descriptionId="alert-editor-desc">
          {isEdit ? (
            <div className="mb-4">
              <p className="font-medium">{applicationName || applicationId}</p>
              <p className="flex items-center gap-2 text-xs text-gray-500">
                <span>{applicationId}</span>
                {renderEnvBadge(environment)}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {typeLabel(alertType)}
              </p>
            </div>
          ) : (
            <div className="mb-4 flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm">
                Alert type
                {availableTypes.length > 0 ? (
                  <select
                    className="border box-border h-[34px] px-2"
                    value={alertType}
                    onChange={(e) => setAlertType(e.target.value)}
                  >
                    {availableTypes.map((type) => (
                      <option key={type.key} value={type.key}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="border box-border h-[34px] px-2 bg-gray-50 text-gray-500"
                    value={optionsLoading ? 'Loading…' : 'No alert types available'}
                    disabled
                  />
                )}
              </label>
              <div className="flex flex-col gap-1 text-sm">
                <span>BIT Ids</span>
                <MultiSelect
                  size="small"
                  options={applicationOptions}
                  value={selectedBitIds}
                  onChange={setSelectedBitIds}
                  placeholder="Select by BIT Id or name"
                  itemHeight={48}
                />
                <span className="text-xs text-gray-500">
                  Select one or more applications — each is subscribed in the
                  chosen environment.
                </span>
              </div>
              <label className="flex flex-col gap-1 text-sm">
                Environment
                <select
                  className="border box-border h-[34px] px-2"
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                >
                  {ENVIRONMENTS.map((env) => (
                    <option key={env} value={env}>
                      {env}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          <p className="mb-1 text-xs uppercase tracking-wide text-gray-500">
            Channels
          </p>
          {optionsReady ? (
            <CheckboxGroupField size="small">
              <div className="flex flex-row flex-wrap gap-x-6 gap-y-2">
                {availableChannels.map((channel) => (
                  <CheckboxField key={channel} label={channelLabel(channel)}>
                    <input
                      type="checkbox"
                      name={`channel-${channel}`}
                      checked={channels.includes(channel)}
                      disabled={submitting}
                      onChange={() => toggleChannel(channel)}
                    />
                  </CheckboxField>
                ))}
              </div>
            </CheckboxGroupField>
          ) : optionsLoading ? (
            <p className="text-sm text-gray-500">Loading channels…</p>
          ) : (
            <p className="text-sm text-red-600">
              Notification channels are unavailable right now.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button type="button" onClick={closeEditor} disabled={submitting}>
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                >
                  {isEdit ? 'Save' : 'Create'}
                  {submitting ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteTarget !== null}
        mobileMode="fullscreen"
        srLabelledBy="alert-delete-title"
        srDescribedBy="alert-delete-desc"
        onUserRequest={() => setDeleteTarget(null)}
      >
        <ModalHeader
          titleId="alert-delete-title"
          onUserRequest={() => setDeleteTarget(null)}
        >
          Remove subscription
        </ModalHeader>
        <ModalBody descriptionId="alert-delete-desc">
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <IconAlertTriangle size={20} className="mt-0.5 shrink-0 text-red-500" />
            <span>
              Remove the{' '}
              <span className="font-semibold">
                {alertTypeLabel(deleteTarget?.alertType ?? '').toLowerCase()}
              </span>{' '}
              alert for{' '}
              <span className="font-semibold">
                {deleteTarget?.details?.application_name ||
                  deleteTarget?.details?.application_id}
              </span>{' '}
              in{' '}
              <span className="font-semibold uppercase">
                {deleteTarget?.details?.environment}
              </span>
              ? You will stop receiving these notifications.
            </span>
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup mode="static">
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField mode="text">
                <button type="button" onClick={() => setDeleteTarget(null)}>
                  Cancel
                </button>
              </ButtonField>
            </ButtonGroupItem>
            <ButtonGroupItem slot="buttonGroupItem">
              <ButtonField>
                <button
                  type="button"
                  onClick={() =>
                    deleteTarget && deleteMutation.mutate(deleteTarget.id)
                  }
                  disabled={deleteMutation.isPending}
                >
                  Remove
                  {deleteMutation.isPending ? (
                    <IconLoader2 size={16} className="animate-spin" />
                  ) : null}
                </button>
              </ButtonField>
            </ButtonGroupItem>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AlertsPage;
